import React, { Fragment, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { NetworkStatus } from '@apollo/client';
import { Card, Grid, Typography, ListItemText } from '@material-ui/core';

import { Dimensions, ErrorComponent, JwlTable, LoadingSpinner } from '../../../../components';
import { PublishableStatusComponent } from '../../../../components/PublishableStatusComponent';
import { userLanguage } from '../../../../core/AuthContext';

import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';
import PieceInventoryStatsWidget from '../PieceInventoryStatsWidget';
import { GQLDeliveryUnit, GQLParty } from '@jewelutions/core-crm-server-api-client-domain';
import { ProducerNavigation } from '../../navigation/ProducerNavigation';

interface IProps {
  party: GQLParty;
  deliveryUnits: GQLDeliveryUnit[];
}

const PieceDetails: React.FC<IProps> = ({ party, deliveryUnits }: IProps) => {
  const [errorComponentState, setErrorComponentState] = useState(false);

  const pieceId = ProducerNavigation.pieceDetails.paramsFromPath(useRouteMatch()).pieceId;

  const { data, loading, error, networkStatus, refetch } = GQLOperationBuilder.piece.getPiece({
    pieceId: pieceId
  });

  const reloadData = () => {
    refetch().then();
  };

  if (loading || networkStatus === NetworkStatus.refetch) return <LoadingSpinner />;

  if (error && !errorComponentState)
    return (
      <ErrorComponent
        close={setErrorComponentState}
        details={error?.stack}
        callback={reloadData}
        message={error.message}
      />
    );

  const getPieceModelsRows = () => {
    return GQLOperationBuilder.piece.pieceAssetFromResponse(data).piece.models.map(pieceModel => {
      return {
        id: pieceModel.id,
        cells: [
          pieceModel.sizeType,
          pieceModel.stonesPlacementType,
          <Dimensions dimensions3D={pieceModel.dimensions} />
        ]
      };
    });
  };

  return (
    <Grid container direction="row">
      <Grid item md={6} style={{ marginBottom: '8px' }}>
        <Card style={{ padding: '8px' }}>
          <div style={{ display: 'flex' }}>
            <Typography style={{ marginRight: '2px' }} variant={'h6'}>
              Piece Details
            </Typography>
          </div>
          <ListItemText
            primary="Piece name"
            secondary={GQLOperationBuilder.piece.pieceAssetFromResponse(data).piece.title[userLanguage]}
          />
          <ListItemText
            primary="Piece description"
            secondary={GQLOperationBuilder.piece.pieceAssetFromResponse(data).piece.description[userLanguage]}
          />
          <ListItemText
            primary="Geometry"
            secondary={GQLOperationBuilder.piece.pieceAssetFromResponse(data).piece.geometryType}
          />
        </Card>
      </Grid>

      {deliveryUnits.map(deliveryUnit => {
        return (
          <PieceInventoryStatsWidget
            key={deliveryUnit.id}
            party={party}
            deliveryUnit={deliveryUnit}
            pieceId={pieceId}
          />
        );
      })}

      <Fragment>
        <JwlTable
          title="Piece Models"
          description={`Piece models available for ${
            GQLOperationBuilder.piece.pieceAssetFromResponse(data).piece.title[userLanguage]
          }`}
          columns={['Size', 'Stones', `Dimensions`]}
          rows={getPieceModelsRows()}
        />
      </Fragment>
    </Grid>
  );
};

export default PieceDetails;
